import { useState, useRef, useEffect } from 'react';
import { Upload, X, Camera, Image as ImageIcon } from 'lucide-react';
import { uploadLogo, deleteLogo } from '../utils/api';
import { API_URL } from '../utils/api';

export default function LogoUpload({ barbershopId, currentLogo, onLogoChange, className = "" }) {
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(currentLogo || null);
  const fileInputRef = useRef(null);

  // Atualizar preview quando currentLogo mudar
  useEffect(() => {
    setPreview(currentLogo || null);
  }, [currentLogo]);

  // Carregar logo do localStorage como backup
  useEffect(() => {
    const savedLogo = localStorage.getItem(`barbershop_${barbershopId}_logo`);
    if (savedLogo && !currentLogo) {
      setPreview(savedLogo);
    }
  }, [barbershopId, currentLogo]);

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Validar tipo de arquivo
      if (!file.type.startsWith('image/')) {
        alert('Por favor, selecione apenas arquivos de imagem.');
        return;
      }

      // Validar tamanho (5MB)
      if (file.size > 5 * 1024 * 1024) {
        alert('A imagem deve ter no máximo 5MB.');
        return;
      }

      // Criar preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setPreview(e.target.result);
      };
      reader.readAsDataURL(file);

      // Fazer upload
      handleUpload(file);
    }
  };

  const handleUpload = async (file) => {
    setUploading(true);
    try {
      const response = await uploadLogo(barbershopId, file);
      
      // Atualizar preview com a URL do servidor
      setPreview(response.data.logoUrl);

      // Salvar no localStorage como backup
      localStorage.setItem(`barbershop_${barbershopId}_logo`, response.data.logoUrl);

      // Notificar componente pai
      if (onLogoChange) {
        onLogoChange(response.data.logoUrl);
      }
      
      alert('Logo atualizado com sucesso!');
    } catch (error) {
      console.error('❌ Erro ao fazer upload:', error);
      alert('Erro ao fazer upload: ' + (error.response?.data?.error || error.message));
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async () => {
    if (!confirm('Tem certeza que deseja remover o logo?')) {
      return;
    }

    try {
      await deleteLogo(barbershopId);
      setPreview(null);
      
      // Notificar componente pai
      if (onLogoChange) {
        onLogoChange(null);
      }
      
      alert('Logo removido com sucesso!');
    } catch (error) {
      console.error('Erro ao remover logo:', error);
      alert('Erro ao remover logo: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  return (
    <div className={`relative ${className}`}>
      {/* Input oculto */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileSelect}
        className="hidden"
      />

      {/* Preview do logo */}
      <div 
        onClick={handleClick}
        className="relative w-24 h-24 rounded-lg border-2 border-dashed border-gray-600 cursor-pointer hover:border-primary-500 transition-colors overflow-hidden bg-dark-700"
      >
        {preview ? (
          <img 
            src={preview.startsWith('data:') ? preview : `${API_URL}${preview}`}
            alt="Logo da barbearia" 
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="flex flex-col items-center justify-center w-full h-full text-gray-400">
            <Camera className="w-8 h-8 mb-2" />
            <span className="text-xs">Adicionar Logo</span>
          </div>
        )}

        {/* Loading overlay */}
        {uploading && (
          <div className="absolute inset-0 bg-black bg-opacity-75 flex items-center justify-center">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-500"></div>
          </div>
        )}
      </div>

      {/* Botões de ação */}
      {preview && (
        <div className="absolute -top-2 -right-2 flex gap-1">
          <button
            onClick={handleClick}
            className="p-1 bg-primary-600 text-white rounded-full hover:bg-primary-700 transition-colors"
            title="Alterar logo"
          >
            <Upload className="w-4 h-4" />
          </button>
          <button
            onClick={handleDelete}
            className="p-1 bg-red-600 text-white rounded-full hover:bg-red-700 transition-colors"
            title="Remover logo"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Texto de ajuda */}
      <div className="mt-2 text-xs text-gray-400">
        Clique para adicionar ou alterar o logo
      </div>
    </div>
  );
}
